import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const NAV = [
  { section: 'Overview', items: [
    { path:'/', icon:'📊', label:'Dashboard' },
    { path:'/alerts', icon:'🔔', label:'Alerts' },
  ]},
  { section: 'Sales Cycle', items: [
    { path:'/parties', icon:'🏢', label:'Parties' },
    { path:'/sales-orders', icon:'🛒', label:'Sales Orders' },
    { path:'/invoices', icon:'🧾', label:'Sales Invoices' },
  ]},
  { section: 'Purchase Cycle', items: [
    { path:'/purchase-orders', icon:'📦', label:'Purchase Orders' },
    { path:'/grn', icon:'✅', label:'GRN' },
    { path:'/bills', icon:'📑', label:'Purchase Bills' },
  ]},
  { section: 'Shipment', items: [
    { path:'/containers', icon:'🚢', label:'Containers' },
    { path:'/shipping-bills', icon:'📋', label:'Shipping Bills' },
    { path:'/documents', icon:'📄', label:'Trade Documents' },
  ]},
  { section: 'Compliance', items: [
    { path:'/customs', icon:'🛃', label:'Customs / CHA' },
    { path:'/gst', icon:'🧮', label:'GST / ITC' },
    { path:'/dgft', icon:'📜', label:'DGFT Licences' },
    { path:'/eway-bills', icon:'🛣️', label:'E-Way Bills' },
  ]},
  { section: '📒 Accounts', items: [
    { path:'/accounts', icon:'📒', label:'Accounts Overview' },
    { path:'/accounts/chart', icon:'📂', label:'Chart of Accounts' },
    { path:'/accounts/vouchers', icon:'📝', label:'Journal / Vouchers' },
    { path:'/accounts/ledger', icon:'📖', label:'Ledger' },
    { path:'/accounts/day-book', icon:'📅', label:'Day Book' },
    { path:'/accounts/trial-balance', icon:'⚖️', label:'Trial Balance' },
    { path:'/accounts/profit-loss', icon:'📊', label:'Profit & Loss' },
    { path:'/accounts/balance-sheet', icon:'🏛️', label:'Balance Sheet' },
    { path:'/accounts/cash-flow', icon:'💧', label:'Cash Flow' },
    { path:'/accounts/tds', icon:'🧾', label:'TDS' },
  ]},
  { section: 'Reports', items: [
    { path:'/order-trail', icon:'🔗', label:'Order Trail' },
    { path:'/receivables', icon:'💚', label:'Receivables' },
    { path:'/payables', icon:'🔴', label:'Payables' },
    { path:'/mis', icon:'📈', label:'MIS Reports' },
  ]},
  { section: 'Master', items: [
    { path:'/products', icon:'🔧', label:'Products / HS Codes' },
    { path:'/settings', icon:'⚙️', label:'Settings' },
  ]},
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate  = useNavigate();
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-58 bg-slate-900 flex flex-col transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} style={{width:'230px',minWidth:'230px'}}>
        <div className="px-4 py-4 border-b border-slate-700/50 flex-shrink-0">
          <div className="font-black text-lg text-amber-400 tracking-tight">⚓ TradeFlow</div>
          <div className="text-xs text-slate-500 mt-0.5 tracking-widest uppercase">IEC ERP</div>
        </div>
        <div className="mx-3 my-2 bg-slate-800 rounded-lg px-3 py-2 flex-shrink-0">
          <div className="text-xs font-semibold text-white truncate">Mehta Global Exports</div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">IEC: 0512034567</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-2">
          {NAV.map(sec => (
            <div key={sec.section}>
              <div className={`text-xs font-bold uppercase tracking-widest px-2 py-1 ${sec.section.startsWith('📒') ? 'text-amber-400' : 'text-slate-500'}`}>{sec.section}</div>
              {sec.items.map(item => {
                const active = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${active ? 'bg-amber-400/15 text-amber-400 border-l-2 border-amber-400 pl-2.5' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                    <span className="text-sm w-4 text-center">{item.icon}</span>
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-slate-700/50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0"><div className="text-xs font-semibold text-white truncate">{user?.name}</div><div className="text-xs text-slate-500 truncate">{user?.role}</div></div>
            <button onClick={handleLogout} className="text-slate-500 hover:text-red-400 text-xs" title="Logout">⎋</button>
          </div>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-4 h-13 flex items-center gap-3 flex-shrink-0 shadow-sm" style={{height:'52px'}}>
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100"><span className="text-slate-600 text-lg">☰</span></button>
          <div className="font-black text-slate-800 text-sm lg:hidden">⚓ TradeFlow</div>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:block text-xs text-slate-400">{new Date().toLocaleDateString('en-IN',{weekday:'short',day:'2-digit',month:'short',year:'numeric'})}</span>
            <span className="text-xs text-emerald-600 font-bold">● LIVE</span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-5">{children}</main>
      </div>
    </div>
  );
}
